import MockAdapter from 'axios-mock-adapter';
import { AxiosInstance } from 'axios';
import {
    AccountLink,
    ShareJob,
    ShareJobActivity,
    ShareJobActivityErrorCode,
    ShareJobActivityStatusEnum,
} from 'oas/datasharing';
import { setupShareAgreementsMock, setupSharerAthleteMock } from '../share-agreements/mocks';

// Listed in GET so it appears in the UI, but every mutation against it returns 404
// to emulate a connection that was deleted by the other account between page load and action.
const STALE_ACCOUNT_LINK_ID = 'stale-account-link-test';

// Mirrors backend behaviour: each approved connection is stored as two records,
// one on each account, with sharer/recipient swapped. resolveLinkPair needs both.
const generateReciprocalLink = (link: AccountLink): AccountLink | undefined => {
    if (
        !link.is_approved ||
        link.sharer_account_id === undefined ||
        link.sharer_account_region === undefined ||
        link.sharer_account_name === undefined ||
        link.sharer_link_name === undefined
    ) {
        return undefined;
    }
    return {
        ...link,
        id: `${link.id}-reciprocal`,
        sharer_account_region: link.recipient_account_region,
        sharer_account_id: link.recipient_account_id,
        sharer_account_name: link.recipient_account_name,
        sharer_link_name: link.recipient_link_name,
        recipient_account_region: link.sharer_account_region,
        recipient_account_id: link.sharer_account_id,
        recipient_account_name: link.sharer_account_name,
        recipient_link_name: link.sharer_link_name,
    };
};

// Function to generate mock account links based on current user ID
const generateMockAccountLinks = (currentAccountId: number): AccountLink[] => {
    const baseLinks: AccountLink[] = [
        // Pending connection - recipient generated code, sharer hasn't used it yet
        {
            id: 'pending-1',
            sharer_account_region: undefined,
            sharer_account_id: undefined,
            sharer_account_name: undefined,
            sharer_link_name: undefined,
            recipient_account_region: 'EU',
            recipient_account_id: currentAccountId,
            recipient_account_name: 'My Account',
            recipient_link_name: 'England mens national squad',
            code: 'VY6D3-2234-CAT5',
            is_approved: false,
            redact_activity_names: false,
            redact_period_names: false,
            created_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(), // 5 days ago
            modified_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        },
        // Connected - current user is sharer (sending data)
        {
            id: '1a2b3c4d-5e6f-7g8h-9i0j-k1l2m3n4o5p6',
            sharer_account_region: 'EU',
            sharer_account_id: currentAccountId,
            sharer_account_name: 'My Account',
            sharer_link_name: 'Performance Analytics Connection',
            recipient_account_region: 'EU',
            recipient_account_id: 1002,
            recipient_account_name: 'Performance Analytics Ltd',
            recipient_link_name: 'Performance Analytics Ltd',
            code: 'LINK-ABC123',
            is_approved: true,
            redact_activity_names: false,
            redact_period_names: false,
            created_at: '2024-01-15T10:30:00Z',
            modified_at: '2024-01-15T10:30:00Z',
        },
        // Stale connection: appears in the list but every mutation returns 404 (DW-3355 test)
        {
            id: STALE_ACCOUNT_LINK_ID,
            sharer_account_region: 'EU',
            sharer_account_id: currentAccountId,
            sharer_account_name: 'My Account',
            sharer_link_name: 'Stale Test Connection (will 404)',
            recipient_account_region: 'EU',
            recipient_account_id: 1099,
            recipient_account_name: 'Deleted Other Account',
            recipient_link_name: 'Stale Test Connection (will 404)',
            code: 'LINK-STALE1',
            is_approved: true,
            redact_activity_names: false,
            redact_period_names: false,
            created_at: '2024-01-01T00:00:00Z',
            modified_at: '2024-01-01T00:00:00Z',
        },
        // Connected - current user is recipient (receiving data)
        {
            id: '2b3c4d5e-6f7g-8h9i-0j1k-l2m3n4o5p6q7',
            sharer_account_region: 'US',
            sharer_account_id: 1003,
            sharer_account_name: 'LA Galaxy',
            sharer_link_name: 'LA Galaxy Training Data',
            recipient_account_region: 'EU',
            recipient_account_id: currentAccountId,
            recipient_account_name: 'My Account',
            recipient_link_name: 'LA Galaxy',
            code: 'LINK-DEF456',
            is_approved: true,
            redact_activity_names: false,
            redact_period_names: true,
            created_at: '2024-02-20T14:15:00Z',
            modified_at: '2024-02-20T14:15:00Z',
        },
        // Not approved yet - current user is sharer, recipient used code but not approved yet
        {
            id: '3c4d5e6f-7g8h-9i0j-1k2l-m3n4o5p6q7r8',
            sharer_account_region: 'AU',
            sharer_account_id: currentAccountId,
            sharer_account_name: 'My Account',
            sharer_link_name: 'AFL Research Connection',
            recipient_account_region: 'AU',
            recipient_account_id: 1006,
            recipient_account_name: 'AFL Research Center',
            recipient_link_name: 'AFL Research Center',
            code: 'LINK-GHI789',
            is_approved: false,
            redact_activity_names: true,
            redact_period_names: true,
            created_at: '2024-03-10T08:45:00Z',
            modified_at: '2024-03-10T08:45:00Z',
        },
        // Connected - current user is recipient (receiving data)
        {
            id: '4d5e6f7g-8h9i-0j1k-2l3m-n4o5p6q7r8s9',
            sharer_account_region: 'EU',
            sharer_account_id: 1007,
            sharer_account_name: 'Bayern Munich',
            sharer_link_name: 'Bayern Munich Performance',
            recipient_account_region: 'EU',
            recipient_account_id: currentAccountId,
            recipient_account_name: 'My Account',
            recipient_link_name: 'Bayern Munich',
            code: 'LINK-JKL012',
            is_approved: true,
            redact_activity_names: false,
            redact_period_names: false,
            created_at: '2024-04-05T12:00:00Z',
            modified_at: '2024-04-05T12:00:00Z',
        },
        // Not approved yet - current user is sharer, recipient used code but not approved yet
        {
            id: '5e6f7g8h-9i0j-1k2l-3m4n-o5p6q7r8s9t0',
            sharer_account_region: 'US',
            sharer_account_id: currentAccountId,
            sharer_account_name: 'My Account',
            sharer_link_name: 'NFL Analytics Connection',
            recipient_account_region: 'US',
            recipient_account_id: 1010,
            recipient_account_name: 'NFL Analytics Division',
            recipient_link_name: 'NFL Analytics Division',
            code: 'LINK-MNO345',
            is_approved: false,
            redact_activity_names: true,
            redact_period_names: false,
            created_at: '2024-05-12T16:20:00Z',
            modified_at: '2024-05-12T16:20:00Z',
        },
    ];

    const reciprocalLinks = baseLinks
        .map(generateReciprocalLink)
        .filter((link): link is AccountLink => link !== undefined);

    return [...baseLinks, ...reciprocalLinks];
};

// state outside of the function to persist across calls
let mockAccountLinks: AccountLink[] = [];
let isInitialized = false;

export function setupDatasharingMock(axiosInstance: AxiosInstance, _currentAccountId?: number) {
    const mock = new MockAdapter(axiosInstance, { onNoMatch: 'passthrough' });
    const accountId = _currentAccountId ?? 1;

    // initialise mockAccountLinks only once
    if (!isInitialized) {
        mockAccountLinks = generateMockAccountLinks(accountId);
        isInitialized = true;
    }

    // Get account links
    mock.onGet(`${process.env.REACT_APP_DATASHARING_API_URL}/account-links`).reply(() => {
        return [200, mockAccountLinks];
    });

    // Setup mock data for: agreements, sharer athletes
    setupShareAgreementsMock(mock);
    setupSharerAthleteMock(mock);

    // generate linking Code - 50/50 success/fail
    mock.onPost(`${process.env.REACT_APP_DATASHARING_API_URL}/generate-linking-code`).reply((config) => {
        const shouldSucceed = Math.random() > 0.5;

        if (shouldSucceed) {
            const requestData = JSON.parse(config.data);
            const newLink: AccountLink = {
                id: crypto.randomUUID(),
                sharer_account_region: undefined,
                sharer_account_id: undefined,
                sharer_account_name: undefined,
                sharer_link_name: undefined,
                recipient_account_region: 'EU',
                recipient_account_id: accountId,
                recipient_account_name: 'Mock Account',
                recipient_link_name: requestData.recipient_link_name || 'New Connection',
                code: `MOCK-${Math.random().toString(36).substring(2, 8).toUpperCase()}`,
                is_approved: false,
                redact_activity_names: false,
                redact_period_names: false,
                created_at: new Date().toISOString(),
                modified_at: new Date().toISOString(),
            };

            // Add the new link to the mock data
            mockAccountLinks.push(newLink);

            return [201, newLink];
        } else {
            return [
                500,
                {
                    request_id: crypto.randomUUID(),
                    reason: 'Internal server error',
                },
            ];
        }
    });

    // Validate linking code
    // Special test codes for error states (all fail at validation):
    //   EXPR-EXPR-EXPR  -> expired code
    //   INVL-INVL-INVL  -> invalid code
    //   USED-USED-USED  -> code already used
    //   SELF-SELF-SELF  -> cannot approve your own link
    //   DUPL-DUPL-DUPL  -> duplicate account link
    //   FAIL-FAIL-FAIL  -> server error
    mock.onPost(`${process.env.REACT_APP_DATASHARING_API_URL}/validate-linking-code`).reply((config) => {
        const requestData = JSON.parse(config.data);
        const code = requestData.code;

        // Error codes that fail at validation
        if (code === 'EXPR-EXPR-EXPR') {
            return [422, { request_id: crypto.randomUUID(), reason: 'code has expired' }];
        }
        if (code === 'INVL-INVL-INVL') {
            return [422, { request_id: crypto.randomUUID(), reason: 'invalid account link code' }];
        }
        if (code === 'USED-USED-USED') {
            return [422, { request_id: crypto.randomUUID(), reason: 'code has already been used' }];
        }
        if (code === 'SELF-SELF-SELF') {
            return [422, { request_id: crypto.randomUUID(), reason: 'cannot approve your own link' }];
        }
        if (code === 'DUPL-DUPL-DUPL') {
            return [422, { request_id: crypto.randomUUID(), reason: 'duplicate account link' }];
        }
        if (code === 'FAIL-FAIL-FAIL') {
            return [500, { request_id: crypto.randomUUID(), reason: 'Internal server error' }];
        }

        // Find account link by code
        const accountLink = mockAccountLinks.find((link) => link.code === code);

        if (accountLink) {
            return [200, accountLink];
        } else {
            // generate a mock valid account link for any other code
            const mockLink: AccountLink = {
                id: crypto.randomUUID(),
                sharer_account_region: 'US',
                sharer_account_id: 9999,
                sharer_account_name: 'Mock Partner Account',
                sharer_link_name: undefined,
                recipient_account_region: 'EU',
                recipient_account_id: accountId,
                recipient_account_name: 'My Account',
                recipient_link_name: 'Link name',
                code: code,
                is_approved: false,
                redact_activity_names: true,
                redact_period_names: true,
                created_at: new Date().toISOString(),
                modified_at: new Date().toISOString(),
            };
            // add the new link to the array so it can be found when approving
            mockAccountLinks.push(mockLink);
            return [200, mockLink];
        }
    });

    // Approve account link
    mock.onPost(new RegExp(`${process.env.REACT_APP_DATASHARING_API_URL}/account-links/.*/approve`)).reply((config) => {
        const urlParts = config.url?.split('/');
        const accountLinkId = urlParts?.[urlParts.length - 2];
        const requestData = JSON.parse(config.data);

        if (accountLinkId === STALE_ACCOUNT_LINK_ID) {
            return [404, { request_id: crypto.randomUUID(), reason: 'Account link not found' }];
        }

        const accountLink = mockAccountLinks.find((link) => link.id === accountLinkId);

        if (accountLink) {
            // Update the account link
            accountLink.is_approved = true;
            if (requestData.sharer_link_name) {
                accountLink.sharer_link_name = requestData.sharer_link_name;
            }
            accountLink.modified_at = new Date().toISOString();

            return [200, accountLink];
        } else {
            return [404, { request_id: crypto.randomUUID(), reason: 'Account link not found' }];
        }
    });

    // Update account link
    mock.onPatch(new RegExp(`${process.env.REACT_APP_DATASHARING_API_URL}/account-links/.*`)).reply((config) => {
        const urlParts = config.url?.split('/');
        const accountLinkId = urlParts?.[urlParts.length - 1];
        const requestData = JSON.parse(config.data);

        if (accountLinkId === STALE_ACCOUNT_LINK_ID) {
            return [404, { request_id: crypto.randomUUID(), reason: 'Account link not found' }];
        }

        const accountLink = mockAccountLinks.find((link) => link.id === accountLinkId);

        if (accountLink) {
            // update the account link with provided fields
            if (requestData.sharer_link_name !== undefined) {
                accountLink.sharer_link_name = requestData.sharer_link_name;
            }
            if (requestData.recipient_link_name !== undefined) {
                accountLink.recipient_link_name = requestData.recipient_link_name;
            }
            if (requestData.redact_activity_names !== undefined) {
                accountLink.redact_activity_names = requestData.redact_activity_names;
            }
            if (requestData.redact_period_names !== undefined) {
                accountLink.redact_period_names = requestData.redact_period_names;
            }
            accountLink.modified_at = new Date().toISOString();

            return [200, accountLink];
        } else {
            return [404, { request_id: crypto.randomUUID(), reason: 'Account link not found' }];
        }
    });

    // Delete account link
    mock.onDelete(new RegExp(`${process.env.REACT_APP_DATASHARING_API_URL}/account-links/.*`)).reply((config) => {
        const urlParts = config.url?.split('/');
        const accountLinkId = urlParts?.[urlParts.length - 1];

        if (accountLinkId === STALE_ACCOUNT_LINK_ID) {
            return [404, { request_id: crypto.randomUUID(), reason: 'Account link not found' }];
        }

        if (accountLinkId) {
            const indexToDelete = mockAccountLinks.findIndex((link) => link.id === accountLinkId);

            if (indexToDelete !== -1) {
                mockAccountLinks.splice(indexToDelete, 1);
                return [204];
            } else {
                return [404, { request_id: crypto.randomUUID(), reason: 'Account link not found' }];
            }
        }

        return [400, { request_id: crypto.randomUUID(), reason: 'Invalid account link ID' }];
    });

    // Get share jobs for a share agreement
    const mockShareJobs: ShareJob[] = [
        {
            id: 'job-1a',
            share_agreement_id: 'share-1',
            status: 'success',
            requested_at: '2025-01-10T08:00:00Z',
            started_at: '2025-01-10T08:01:00Z',
            finished_at: '2025-01-10T08:05:30Z',
            from_timestamp: '2024-09-01T00:00:00Z',
            to_timestamp: '2024-12-01T00:00:00Z',
        },
        {
            id: 'job-1b',
            share_agreement_id: 'share-1',
            status: 'success',
            requested_at: '2025-02-15T14:00:00Z',
            started_at: '2025-02-15T14:01:00Z',
            finished_at: '2025-02-15T14:12:45Z',
            from_timestamp: '2024-09-01T00:00:00Z',
            to_timestamp: '2024-12-01T00:00:00Z',
        },
        {
            id: 'job-2a',
            share_agreement_id: 'share-2',
            status: 'success',
            requested_at: '2025-02-15T14:00:00Z',
            started_at: '2025-02-15T14:01:00Z',
            finished_at: '2025-02-15T14:10:20Z',
            from_timestamp: '2024-09-01T00:00:00Z',
            to_timestamp: '2024-12-01T00:00:00Z',
        },
        {
            id: 'job-8a',
            share_agreement_id: 'share-8',
            status: 'partial',
            requested_at: '2025-01-20T10:00:00Z',
            started_at: '2025-01-20T10:01:00Z',
            finished_at: '2025-01-20T10:08:15Z',
            error_summary: 'Some entities failed to import',
            from_timestamp: '2024-12-12T00:00:00Z',
            to_timestamp: '2025-02-12T00:00:00Z',
        },
        {
            id: 'job-9a',
            share_agreement_id: 'share-9',
            status: 'running',
            requested_at: '2025-03-01T09:00:00Z',
            started_at: '2025-03-01T09:01:00Z',
            finished_at: '2025-03-01T09:15:00Z',
            from_timestamp: '2024-07-01T00:00:00Z',
            to_timestamp: '2025-01-01T00:00:00Z',
        },
    ];

    mock.onGet(new RegExp(`${process.env.REACT_APP_DATASHARING_API_URL}/share-jobs$`)).reply(() => {
        return [200, mockShareJobs];
    });

    // Start share job
    mock.onPost(new RegExp(`${process.env.REACT_APP_DATASHARING_API_URL}/share-agreements/.*/jobs`)).reply((config) => {
        const urlParts = config.url?.split('/');
        const shareAgreementId = urlParts?.[urlParts.length - 2];

        const mockJob = {
            id: crypto.randomUUID(),
            share_agreement_id: shareAgreementId,
            status: 'pending',
            requested_at: new Date().toISOString(),
            from_timestamp: '2024-01-01T00:00:00Z',
            to_timestamp: new Date().toISOString(),
        };

        return [201, [mockJob]];
    });

    mock.onGet(new RegExp(`${process.env.REACT_APP_DATASHARING_API_URL}/share-agreements/.*/activity-status`)).reply(
        (config) => {
            const urlParts = config.url?.split('/');
            const shareJobId = urlParts?.[urlParts.length - 2];
            const statuses = ['pending', 'running', 'success', 'failed', 'partial', 'skipped'];
            const errorCodes = [
                'err_timeout',
                'raw_file_activity_error',
                'live_file_activity_error',
                'activity_not_found',
                'activity_exists',
                'no_pip',
                'pip_overlap',
                'error123',
                null,
            ];

            const mockActivity: ShareJobActivity = {
                id: crypto.randomUUID(),
                activity_id: crypto.randomUUID(),
                share_job_id: shareJobId ?? '',
                activity_name: 'Activity lol 12342340234329',
                status: statuses[Math.floor(Math.random() * statuses.length)] as ShareJobActivityStatusEnum,
                attempt_count: 0,
                created_at: new Date().toISOString(),
                last_error: 'this is an activity error',
                error_code: errorCodes[Math.floor(Math.random() * errorCodes.length)] as ShareJobActivityErrorCode,
                started_at: new Date().toISOString(),
                activity_start_time: new Date().toISOString(),
            };

            const mockActivity2: ShareJobActivity = {
                id: crypto.randomUUID(),
                activity_id: crypto.randomUUID(),
                share_job_id: shareJobId ?? '',
                activity_name: 'Activity lol 4568283032039',
                status: statuses[Math.floor(Math.random() * statuses.length)] as ShareJobActivityStatusEnum,
                attempt_count: 0,
                created_at: new Date().toISOString(),
                last_error: 'this is another activity error',
                started_at: new Date().toISOString(),
                activity_start_time: new Date().toISOString(),
            };

            return [200, [mockActivity, mockActivity2]];
        },
    );
}
